import { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import type { AgendaEventCategoryMeta, AgendaRosterTemplate, AgendaTaakGroup } from "@domaddress/shared";

type GroupRow = { id: number; name: string };
type TaakRow = { id: number; name: string };

type RoleDraft = {
  role_name: string;
  werkgroep_group_id: string;
  quantity: string;
};

export default function RosterTemplatesPage() {
  const [categories, setCategories] = useState<AgendaEventCategoryMeta[]>([]);
  const [groups, setGroups] = useState<GroupRow[]>([]);
  const [taken, setTaken] = useState<TaakRow[]>([]);
  const [selectedKey, setSelectedKey] = useState("");
  const [label, setLabel] = useState("");
  const [roles, setRoles] = useState<RoleDraft[]>([]);
  const [newCategory, setNewCategory] = useState("");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const [taakGroups, setTaakGroups] = useState<AgendaTaakGroup[]>([]);
  const [editingGroupId, setEditingGroupId] = useState<number | "new" | null>(null);
  const [groupName, setGroupName] = useState("");
  const [groupMembers, setGroupMembers] = useState<string[]>([]);
  const [memberDraft, setMemberDraft] = useState("");
  const [groupSaving, setGroupSaving] = useState(false);

  const loadTaakGroups = useCallback(async () => {
    const res = await api.get<{ data: AgendaTaakGroup[] }>("/api/taak-groups");
    setTaakGroups(res.data.data);
  }, []);

  const loadCategories = useCallback(async () => {
    const res = await api.get<{ data: AgendaEventCategoryMeta[] }>("/api/roster-templates/categories");
    setCategories(res.data.data);
  }, []);

  const loadMeta = useCallback(async () => {
    const [gRes, tRes] = await Promise.all([
      api.get<{ data: GroupRow[] }>("/api/meta/groups"),
      api.get<{ data: TaakRow[] }>("/api/meta/taken"),
    ]);
    setGroups(gRes.data.data);
    setTaken(tRes.data.data);
  }, []);

  const loadTemplate = useCallback(async (key: string) => {
    if (!key) return;
    const res = await api.get<{ data: AgendaRosterTemplate }>(`/api/roster-templates/${encodeURIComponent(key)}`);
    const t = res.data.data;
    setLabel(t.label ?? key);
    setRoles(
      t.roles.length > 0
        ? t.roles.map((r) => ({
            role_name: r.role_name,
            werkgroep_group_id: r.werkgroep_group_id != null ? String(r.werkgroep_group_id) : "",
            quantity: String(r.quantity),
          }))
        : []
    );
  }, []);

  useEffect(() => {
    void loadCategories();
    void loadMeta();
    void loadTaakGroups();
  }, [loadCategories, loadMeta, loadTaakGroups]);

  useEffect(() => {
    if (selectedKey) void loadTemplate(selectedKey);
  }, [selectedKey, loadTemplate]);

  function selectCategory(key: string) {
    setSelectedKey(key);
    setMessage("");
    setError("");
  }

  function addCategory(e: React.FormEvent) {
    e.preventDefault();
    const key = newCategory.trim().toLowerCase().replace(/\s+/g, "-");
    if (!key) return;
    if (!categories.some((c) => c.category_key === key)) {
      setCategories([...categories, { category_key: key, label: key, has_template: false }]);
    }
    setNewCategory("");
    selectCategory(key);
    setLabel(key);
    setRoles([]);
  }

  function addRole() {
    setRoles([...roles, { role_name: "", werkgroep_group_id: "", quantity: "1" }]);
  }

  function updateRole(idx: number, patch: Partial<RoleDraft>) {
    setRoles(roles.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  }

  function removeRole(idx: number) {
    setRoles(roles.filter((_, i) => i !== idx));
  }

  async function save() {
    if (!selectedKey) return;
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const payload = {
        label: label.trim() || selectedKey,
        roles: roles
          .filter((r) => r.role_name.trim())
          .map((r) => ({
            role_name: r.role_name.trim(),
            werkgroep_group_id: r.werkgroep_group_id ? Number(r.werkgroep_group_id) : null,
            quantity: Math.max(1, Number(r.quantity) || 1),
          })),
      };
      await api.put(`/api/roster-templates/${encodeURIComponent(selectedKey)}`, payload);
      setMessage("Sjabloon opgeslagen.");
      await loadCategories();
    } catch {
      setError("Opslaan mislukt.");
    } finally {
      setSaving(false);
    }
  }

  function startNewGroup() {
    setEditingGroupId("new");
    setGroupName("");
    setGroupMembers([]);
    setMemberDraft("");
  }

  function editGroup(g: AgendaTaakGroup) {
    setEditingGroupId(g.id);
    setGroupName(g.name);
    setGroupMembers([...g.members]);
    setMemberDraft("");
  }

  function addMemberToGroup() {
    const m = memberDraft.trim();
    if (!m || groupMembers.some((x) => x.toLowerCase() === m.toLowerCase())) return;
    setGroupMembers([...groupMembers, m]);
    setMemberDraft("");
  }

  async function saveTaakGroup() {
    if (!groupName.trim()) return;
    setGroupSaving(true);
    try {
      const payload = { name: groupName.trim(), members: groupMembers };
      if (editingGroupId === "new") {
        await api.post("/api/taak-groups", payload);
      } else if (typeof editingGroupId === "number") {
        await api.put(`/api/taak-groups/${editingGroupId}`, payload);
      }
      setEditingGroupId(null);
      await loadTaakGroups();
    } catch {
      alert("Taakgroep opslaan mislukt. Bestaat de naam misschien al?");
    } finally {
      setGroupSaving(false);
    }
  }

  async function deleteTaakGroup(id: number) {
    if (!window.confirm("Deze taakgroep verwijderen?")) return;
    await api.delete(`/api/taak-groups/${id}`);
    if (editingGroupId === id) setEditingGroupId(null);
    await loadTaakGroups();
  }

  const currentCat = categories.find((c) => c.category_key === selectedKey);

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1 className="page-title">Rooster-sjablonen</h1>
          <p className="page-subtitle">
            Stel per evenementtype (zoals <em>dienst</em>, <em>extern</em>) in welke taken standaard nodig
            zijn. Bij sync uit WordPress worden deze automatisch op nieuwe evenementen gezet (als er nog geen
            taken staan).
          </p>
        </div>
      </header>

      <div className="card roster-taak-groups-card">
        <h2>Taakgroepen</h2>
        <p className="roster-template-hint">
          Voeg taken samen voor de pagina <strong>Roosters</strong>, bijvoorbeeld
          &quot;Streaming-Beeld&quot; en &quot;Streaming-Audio&quot; → groep &quot;streaming&quot;.
        </p>
        <ul className="roster-taak-groups-list">
          {taakGroups.map((g) => (
            <li key={g.id}>
              <button type="button" className="roster-taak-group-btn" onClick={() => editGroup(g)}>
                <strong>{g.name}</strong>
                <span className="roster-taak-group-members">{g.members.join(" · ")}</span>
              </button>
              <button
                type="button"
                className="btn-text-danger btn-sm"
                onClick={() => void deleteTaakGroup(g.id)}
              >
                Verwijderen
              </button>
            </li>
          ))}
        </ul>
        {taakGroups.length === 0 && !editingGroupId && (
          <p style={{ color: "var(--text-500)" }}>Nog geen taakgroepen.</p>
        )}
        {!editingGroupId && (
          <button type="button" className="btn-secondary" onClick={startNewGroup}>
            Nieuwe taakgroep
          </button>
        )}
        {editingGroupId != null && (
          <div className="roster-taak-group-editor">
            <div className="form-row">
              <label>Naam van de groep</label>
              <input
                placeholder="bijv. streaming"
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
              />
            </div>
            <div className="form-row">
              <label>Taken in deze groep</label>
              <div className="roster-taak-group-add-member">
                <input
                  list="taken-suggest-groups"
                  placeholder="Taak uit adresboek"
                  value={memberDraft}
                  onChange={(e) => setMemberDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addMemberToGroup();
                    }
                  }}
                />
                <button type="button" className="btn-secondary btn-sm" onClick={addMemberToGroup}>
                  Toevoegen
                </button>
              </div>
              <ul className="roster-taak-group-chips">
                {groupMembers.map((m) => (
                  <li key={m}>
                    {m}
                    <button
                      type="button"
                      aria-label={`Verwijder ${m}`}
                      onClick={() => setGroupMembers(groupMembers.filter((x) => x !== m))}
                    >
                      ×
                    </button>
                  </li>
                ))}
              </ul>
            </div>
            <datalist id="taken-suggest-groups">
              {taken.map((t) => (
                <option key={t.id} value={t.name} />
              ))}
            </datalist>
            <div className="roster-templates-actions">
              <button
                type="button"
                className="btn-primary"
                disabled={groupSaving || !groupName.trim()}
                onClick={() => void saveTaakGroup()}
              >
                {groupSaving ? "Opslaan…" : "Groep opslaan"}
              </button>
              <button type="button" className="btn-secondary" onClick={() => setEditingGroupId(null)}>
                Annuleren
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="card roster-templates-layout">
        <aside className="roster-templates-sidebar">
          <h2>Evenementtypes</h2>
          <ul className="roster-templates-category-list">
            {categories.map((c) => (
              <li key={c.category_key}>
                <button
                  type="button"
                  className={`roster-templates-category-btn${selectedKey === c.category_key ? " active" : ""}`}
                  onClick={() => selectCategory(c.category_key)}
                >
                  {c.label || c.category_key}
                  {c.has_template && <span className="event-roster-badge"> sjabloon</span>}
                </button>
              </li>
            ))}
          </ul>
          <form className="roster-templates-add-cat" onSubmit={addCategory}>
            <input
              placeholder="Nieuw type (slug, bv. concert)"
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
            />
            <button type="submit" className="btn-secondary btn-sm">
              Toevoegen
            </button>
          </form>
        </aside>

        <div className="roster-templates-editor">
          {!selectedKey && <p>Kies links een evenementtype om het sjabloon te bewerken.</p>}

          {selectedKey && (
            <>
              <h2>
                Sjabloon: {currentCat?.label ?? selectedKey}
                <code className="roster-templates-key"> {selectedKey}</code>
              </h2>

              <div className="form-row">
                <label>Weergavenaam (optioneel)</label>
                <input value={label} onChange={(e) => setLabel(e.target.value)} />
              </div>

              <h3>Benodigde taken</h3>
              {roles.length === 0 && <p>Nog geen taken in dit sjabloon.</p>}

              {roles.map((r, idx) => (
                <div key={idx} className="roster-role-card roster-template-role-row">
                  <input
                    list="taken-suggest"
                    placeholder="Taak (bv. Streaming-Beeld)"
                    value={r.role_name}
                    onChange={(e) => updateRole(idx, { role_name: e.target.value })}
                  />
                  <select
                    value={r.werkgroep_group_id}
                    onChange={(e) => updateRole(idx, { werkgroep_group_id: e.target.value })}
                  >
                    <option value="">Werkgroep…</option>
                    {groups.map((g) => (
                      <option key={g.id} value={g.id}>
                        {g.name}
                      </option>
                    ))}
                  </select>
                  <input
                    type="number"
                    min={1}
                    className="roster-template-qty"
                    value={r.quantity}
                    onChange={(e) => updateRole(idx, { quantity: e.target.value })}
                    title="Aantal personen"
                  />
                  <button type="button" className="btn-text-danger" onClick={() => removeRole(idx)}>
                    ×
                  </button>
                </div>
              ))}

              <datalist id="taken-suggest">
                {taken.map((t) => (
                  <option key={t.id} value={t.name} />
                ))}
              </datalist>

              <div className="roster-templates-actions">
                <button type="button" className="btn-secondary" onClick={addRole}>
                  Taak toevoegen
                </button>
                <button type="button" className="btn-primary" disabled={saving} onClick={() => void save()}>
                  {saving ? "Opslaan…" : "Sjabloon opslaan"}
                </button>
              </div>

              {message && <p className="form-success">{message}</p>}
              {error && <p className="form-error">{error}</p>}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
